
import {
    doc,
    collection,
    getDoc,
    getDocs,
    setDoc,
    deleteDoc,
    FirestoreDataConverter
} from 'firebase/firestore';
import { CoreFirebaseService } from './CoreFirebaseService';
import {
    OnboardingVersion,
    onboardingVersionConverter
} from '../converters/onboardingConverter';
import type { StepNode } from '@segmently/schema-models/v2';

/**
 * Recursively strip `undefined` values from an object.
 * Firestore rejects documents containing `undefined` at any depth.
 * The V2 schema converter may produce StepNodes with explicit `undefined`
 * for optional fields — this sanitizer removes them before write.
 */
function stripUndefined(obj: unknown): unknown {
    if (obj === null || obj === undefined) return null;
    if (Array.isArray(obj)) return obj.map(stripUndefined);
    if (typeof obj === 'object') {
        const result: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
            if (value !== undefined) {
                result[key] = stripUndefined(value);
            }
        }
        return result;
    }
    return obj;
}

/**
 * StepNodeScreenService
 *
 * Manages V2 screens (StepNode) as a subcollection within an Onboarding Version.
 * Structure: projects/{projectId}/onboardings/{onboardingId}/versions/{versionId}/screensV2/{screenId}
 *
 * This is the active V2 screen document service after the V1 ScreenGraph
 * screen CRUD path was retired.
 */
export class StepNodeScreenService {
    private readonly collectionName = 'onboardings';
    private readonly versionsCollectionName = 'versions';
    private readonly screensCollectionName = 'screensV2';
    private readonly firestore = CoreFirebaseService.getInstance().firestore;
    private readonly versionConverter: FirestoreDataConverter<OnboardingVersion>;

    constructor(
        versionConverter: FirestoreDataConverter<OnboardingVersion> = onboardingVersionConverter
    ) {
        this.versionConverter = versionConverter;
    }

    private getScreensCollectionRef(projectId: string, onboardingId: string, versionId: string) {
        return collection(
            this.firestore,
            `projects/${projectId}/${this.collectionName}/${onboardingId}/${this.versionsCollectionName}/${versionId}/${this.screensCollectionName}`
        );
    }

    private getScreenRef(projectId: string, onboardingId: string, versionId: string, screenId: string) {
        return doc(
            this.firestore,
            `projects/${projectId}/${this.collectionName}/${onboardingId}/${this.versionsCollectionName}/${versionId}/${this.screensCollectionName}`,
            screenId
        );
    }

    /**
     * Get a specific V2 screen from an onboarding version
     */
    async getScreen(
        projectId: string,
        onboardingId: string,
        versionId: string,
        screenId: string
    ): Promise<StepNode | null> {
        try {
            const docRef = this.getScreenRef(projectId, onboardingId, versionId, screenId);
            const docSnap = await getDoc(docRef);

            if (!docSnap.exists()) {
                return null;
            }

            return { ...docSnap.data(), id: docSnap.id } as StepNode;
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'StepNodeScreenService.getScreen');
            throw error;
        }
    }

    /**
     * List all V2 screens for a version
     */
    async listScreens(
        projectId: string,
        onboardingId: string,
        versionId: string
    ): Promise<StepNode[]> {
        try {
            const collectionRef = this.getScreensCollectionRef(projectId, onboardingId, versionId);
            const querySnapshot = await getDocs(collectionRef);

            return querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as StepNode));
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'StepNodeScreenService.listScreens');
            throw error;
        }
    }

    /**
     * Add a new V2 screen to the subcollection
     */
    async addScreen(
        projectId: string,
        onboardingId: string,
        versionId: string,
        step: StepNode
    ): Promise<void> {
        try {
            const docRef = this.getScreenRef(projectId, onboardingId, versionId, step.id);

            // Check if exists
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                throw new Error(`StepNode with ID ${step.id} already exists`);
            }

            await setDoc(docRef, stripUndefined(step) as StepNode);
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'StepNodeScreenService.addScreen');
            throw error;
        }
    }

    /**
     * Update an existing V2 screen
     */
    async updateScreen(
        projectId: string,
        onboardingId: string,
        versionId: string,
        screenId: string,
        updates: Partial<StepNode>
    ): Promise<void> {
        try {
            const docRef = this.getScreenRef(projectId, onboardingId, versionId, screenId);

            // Check existence
            const docSnap = await getDoc(docRef);
            if (!docSnap.exists()) {
                throw new Error(`StepNode with ID ${screenId} not found`);
            }

            await setDoc(docRef, stripUndefined(updates) as Partial<StepNode>, { merge: true });
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'StepNodeScreenService.updateScreen');
            throw error;
        }
    }

    /**
     * Replace an existing V2 screen entirely (no merge).
     * Use this when you have a complete StepNode object and need to overwrite
     * the document without Firestore's deep merge preserving old nested keys.
     */
    async replaceScreen(
        projectId: string,
        onboardingId: string,
        versionId: string,
        screenId: string,
        step: StepNode
    ): Promise<void> {
        try {
            const docRef = this.getScreenRef(projectId, onboardingId, versionId, screenId);

            // Check existence
            const docSnap = await getDoc(docRef);
            if (!docSnap.exists()) {
                throw new Error(`StepNode with ID ${screenId} not found`);
            }

            // Full overwrite — no merge
            await setDoc(docRef, stripUndefined(step) as StepNode);
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'StepNodeScreenService.replaceScreen');
            throw error;
        }
    }

    /**
     * Delete a V2 screen
     */
    async deleteScreen(
        projectId: string,
        onboardingId: string,
        versionId: string,
        screenId: string
    ): Promise<void> {
        try {
            const docRef = this.getScreenRef(projectId, onboardingId, versionId, screenId);
            await deleteDoc(docRef);
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'StepNodeScreenService.deleteScreen');
            throw error;
        }
    }

    /**
     * Copy (duplicate) a V2 screen
     */
    async copyScreen(
        projectId: string,
        onboardingId: string,
        versionId: string,
        sourceScreenId: string,
        newScreenId?: string
    ): Promise<StepNode> {
        try {
            const sourceDocRef = this.getScreenRef(projectId, onboardingId, versionId, sourceScreenId);
            const sourceSnap = await getDoc(sourceDocRef);

            if (!sourceSnap.exists()) {
                throw new Error(`Source StepNode ${sourceScreenId} not found`);
            }

            const sourceStep = sourceSnap.data() as StepNode;
            const finalNewScreenId = newScreenId || `${sourceScreenId}_copy_${Date.now()}`;
            const newDocRef = this.getScreenRef(projectId, onboardingId, versionId, finalNewScreenId);

            const checkSnap = await getDoc(newDocRef);
            if (checkSnap.exists()) {
                throw new Error(`Target StepNode ID ${finalNewScreenId} already exists`);
            }

            const newStep: StepNode = {
                ...sourceStep,
                id: finalNewScreenId,
                name: `${sourceStep.name} (Copy)`
            };

            await setDoc(newDocRef, stripUndefined(newStep) as StepNode);
            return newStep;
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'StepNodeScreenService.copyScreen');
            throw error;
        }
    }
}
